import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../helpers/language-context';
import LanguageSwitcher from './language-switcher';
import SearchModal from './search-modal';

const ChevronDown = ({ className = '' }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`inline-block transition-transform duration-200 ease-out ${className}`}
  >
    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
    <g id="SVGRepo_iconCarrier">
      <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
    </g>
  </svg>
);

const ArrowRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

const Navbar = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(false);
  const { translations } = useLanguage();
  const location = useLocation();
  const navRef = useRef(null);

  const toggleMenu = () => {
    setOpenMenu(!openMenu);
  };

  const toggleSearch = () => {
    setOpenSearch(!openSearch);
  };

  const toggleDropdown = (menu) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
    if (activeDropdown === menu) {
      setOpenDropdown(false);
    } else {
      setOpenDropdown(true);
    }
  };

  const closeAll = () => {
    setOpenMenu(false);
    setOpenSearch(false);
    setActiveDropdown(null);
    setOpenDropdown(false);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  };

  // Close menus when route changes
  useEffect(() => {
    closeAll();
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        if (openMenu) {
          setOpenMenu(false);
          setActiveDropdown(null);
          setOpenDropdown(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenu]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrolling(true);
      } else {
        setScrolling(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <nav
      ref={navRef}
      className={`min-h-16 p-6 m-auto fixed left-1/2 translate-x-[-50%] drop-shadow-xl z-30 duration-500 ${scrolling ? "top-0 w-full rounded-none md:p-6 bg-white w-100" : "fixed top-5 rounded-xl bg-white bg-opacity-30 w-[89%]"
        } ${!scrolling && (openMenu || openSearch || openDropdown) ? "backdrop-blur-md" : "backdrop-blur-none"}`}
    >
      <div className="flex justify-between items-start">
        <div className="flex-shrink-0">
          <Link to="/" className="flex items-center gap-2 md:gap-3" onClick={closeAll}>
            <img src="/assets/images/logo/kssb.png" alt="Semesta Logo" className="h-8 md:h-9 w-auto hover:scale-105 transition-transform duration-300" />
            <div className={`flex flex-col justify-center text-[11px] md:text-[13px] font-cambria font-bold tracking-widest leading-none ${scrolling ? 'text-[#28357B]' : 'text-white'}`}>
              <span className="mb-[3px]">KUMPULAN</span>
              <span>SEMESTA</span>
            </div>
          </Link>
        </div>

        {/* desktop navbar */}
        <div className="hidden lg:block">
          <div className="grid gap-0 py-1" style={{ gridTemplateColumns: 'repeat(6, max-content)' }} onMouseLeave={() => { setOpenDropdown(false); setActiveDropdown(null); }}>
            {/* about us */}
            <div className={`${scrolling ? 'text-gray-700' : 'text-white'} mr-8 flex gap-2 rounded-md text-sm ${openDropdown && activeDropdown === 'about' ? 'font-bold' : 'font-medium'}`}
              onMouseEnter={() => { setActiveDropdown('about'); setOpenDropdown(true); }}
            >
              {translations.navbar.aboutUs}
              <ChevronDown className={`${openDropdown && activeDropdown === 'about' ? 'rotate-180' : ''}`} />
            </div>

            {/* our business */}
            <div className={`${scrolling ? 'text-gray-700' : 'text-white'} mr-8 rounded-md text-sm ${openDropdown && activeDropdown === 'business' ? 'font-bold' : 'font-medium'}`}
              onMouseEnter={() => { setActiveDropdown('business'); setOpenDropdown(true); }}
            >
              {translations.navbar.ourBusiness}
              <ChevronDown className={`${openDropdown && activeDropdown === 'business' ? 'rotate-180' : ''}`} />
            </div>

            {/* investor relations */}
            {/* <div className={`${scrolling ? 'text-gray-700 hover:text-[#28357B]' : 'text-white hover:text-gray-300'} mr-8 rounded-md text-sm font-medium hover:font-bold`}
              onClick={() => toggleDropdown('projects')}>
              {translations.navbar.investorRelations}
            </div> */}

            {/* governance */}
            <Link to="/governance" className={`${scrolling ? 'text-gray-700' : 'text-white'} mr-8 rounded-md text-sm font-medium`}
              onClick={closeAll} onMouseEnter={() => { setOpenDropdown(false); setActiveDropdown(null); }}
            >
              {translations.navbar.governance}
            </Link>

            {/* media */}
            <div className={`${scrolling ? 'text-gray-700' : 'text-white'} mr-8 rounded-md text-sm ${openDropdown && activeDropdown === 'media' ? 'font-bold' : 'font-medium'}`}
              onMouseEnter={() => { setActiveDropdown('media'); setOpenDropdown(true); }}
            >
              {translations.navbar.media}
              <ChevronDown className={`${openDropdown && activeDropdown === 'media' ? 'rotate-180' : ''}`} />
            </div>

            {/* career */}
            <Link to="/career" className={`${scrolling ? 'text-gray-700' : 'text-white'} rounded-md text-sm font-medium`}
              onClick={closeAll} onMouseEnter={() => { setOpenDropdown(false); setActiveDropdown(null); }}
            >
              {translations.navbar.career}
            </Link>

            {/* about us sub menu */}
            <div
              className={`col-span-2 flex flex-col gap-2 overflow-hidden transition-all duration-500 ease-in-out origin-top ${openDropdown && activeDropdown === 'about'
                ? 'max-h-96 opacity-100 mt-4 pb-2'
                : 'max-h-0 opacity-0 mt-0 pb-0'
                }`}
            >
              <div className="flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/about/who-we-are" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.about.whoWeAre}
                </Link>
              </div>
              <div className="hidden flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/about/our-brand" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.about.ourBrand}
                </Link>
              </div>
              <div className="flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/about/our-history" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.about.ourHistory}
                </Link>
              </div>
              <div className="flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/about/our-team" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.about.ourTeam}
                </Link>
              </div>
              <div className="flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/about/membership" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.about.membership}
                </Link>
              </div>
              <div className="flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/about/awards" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.about.awards}
                </Link>
              </div>
            </div>

            {/* our business sub menu */}
            <div className={`col-span-2 col-start-2 flex flex-col gap-2 overflow-hidden transition-all duration-500 ease-in-out origin-top ${openDropdown && activeDropdown === 'business' ? 'max-h-96 opacity-100 mt-4 pb-2' : 'max-h-0 opacity-0 mt-0 pb-0'}`}>
              <div className="flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/services" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.navbar.ourServices}
                </Link>
              </div>
              <div className="flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/services/subsidiary" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.services.subsidiaryCompany}
                </Link>
              </div>
            </div>

            {/* media sub menu */}
            <div className={`col-span-2 col-start-4 flex flex-col gap-2 overflow-hidden transition-all duration-500 ease-in-out origin-top ${openDropdown && activeDropdown === 'media' ? 'max-h-96 opacity-100 mt-4 pb-2' : 'max-h-0 opacity-0 mt-0 pb-0'}`}>

              <div className="flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/media/news-release" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.media.newsReleases}
                </Link>
              </div>
              <div className="flex items-center group">
                <div
                  className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                >
                  <ArrowRight />
                </div>
                <Link to="/media/our-stories" className={`${scrolling ? 'text-gray-700' : 'text-white'} text-sm font-medium`} onClick={closeAll}>
                  {translations.media.ourStories}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* right slot */}
        <div className={`flex items-center space-x-4 ${openSearch ? 'hidden' : 'block'}`}>
          <LanguageSwitcher scrolling={scrolling} />

          {/* search button */}
          <div className="cursor-pointer" onClick={toggleSearch}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={scrolling ? "#333" : "white"} strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </div>

          {/* contact button */}
          <div className="hidden lg:block">
            <Link to="/contact" className={`${scrolling ? 'bg-[#28357B] text-white hover:text-gray-300' : 'bg-white text-[#28357B] hover:text-gray-500'} px-4 py-2 rounded-full text-sm font-medium`} onClick={closeAll}>
              {translations.navbar.contact}
            </Link>
          </div>

          <div className="lg:hidden">
            <button className={scrolling ? "text-gray-700" : "text-white"} onClick={toggleMenu}>
              {!openMenu && (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
              {openMenu && (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      <SearchModal isOpen={openSearch} onClose={() => setOpenSearch(false)} />

      {/* mobile navbar */}
      <div className={`lg:hidden pt-6 ${openMenu ? 'block' : 'hidden'}`}>
        <div className="flex flex-col space-y-3">

          {/* About Us */}
          <div className={`rounded-xl border border-${scrolling ? 'gray-700' : 'white/20'} overflow-hidden transition-all duration-300 ${activeDropdown === 'about' ? 'bg-white/5' : ''}`}>
            <div
              className="flex justify-between items-center p-4 cursor-pointer"
              onClick={() => toggleDropdown('about')}
            >
              <span className={`text-${scrolling ? 'gray-700' : 'white'} font-medium`}>{translations.navbar.aboutUs}</span>
              <div className="w-8 h-8 rounded-full bg-[#FDA729] flex items-center justify-center shrink-0">
                <ChevronDown className={`text-white transform transition-transform duration-200 ${activeDropdown === 'about' ? 'rotate-180' : ''}`} />
              </div>
            </div>
            <div className={`transition-all duration-300 ease-in-out ${activeDropdown === 'about' ? 'max-h-[500px] opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
              <div className="flex flex-col px-4 space-y-3 pl-8">
                <div className="flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/about/who-we-are" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.about.whoWeAre}</Link>
                </div>
                <div className="hidden flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/about/our-brand" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.about.ourBrand}</Link>
                </div>
                <div className="flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/about/our-history" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.about.ourHistory}</Link>
                </div>
                <div className="flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/about/our-team" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.about.ourTeam}</Link>
                </div>
                <div className="flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/about/membership" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.about.membership}</Link>
                </div>
                <div className="flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/about/awards" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.about.awards}</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Our Business */}
          <div className={`rounded-xl border border-${scrolling ? 'gray-700' : 'white/20'} overflow-hidden transition-all duration-300 ${activeDropdown === 'business' ? 'bg-white/5' : ''}`}>
            <div
              className="flex justify-between items-center p-4 cursor-pointer"
              onClick={() => toggleDropdown('business')}
            >
              <span className={`text-${scrolling ? 'gray-700' : 'white'} font-medium`}>{translations.navbar.ourBusiness}</span>
              <div className="w-8 h-8 rounded-full bg-[#FDA729] flex items-center justify-center shrink-0">
                <ChevronDown className={`text-white transform transition-transform duration-200 ${activeDropdown === 'business' ? 'rotate-180' : ''}`} />
              </div>
            </div>
            <div className={`transition-all duration-300 ease-in-out ${activeDropdown === 'business' ? 'max-h-40 opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
              <div className="flex flex-col px-4 space-y-3 pl-8">
                <div className="flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/services" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.navbar.ourServices}</Link>
                </div>
                <div className="flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/services/subsidiary" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.services.subsidiaryCompany}</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Governance - Single Link but styled like others */}
          <Link to="/governance" className={`rounded-xl border border-${scrolling ? 'gray-700' : 'white/20'} p-4 flex justify-between items-center text-${scrolling ? 'gray-700' : 'white'} font-medium`} onClick={closeAll}>
            {translations.navbar.governance}
            <div className="w-8 h-8 rounded-full bg-[#FDA729] flex items-center justify-center shrink-0 text-white">
              <ArrowRight />
            </div>
          </Link>

          {/* Media */}
          <div className={`rounded-xl border border-${scrolling ? 'gray-700' : 'white/20'} overflow-hidden transition-all duration-300 ${activeDropdown === 'media' ? 'bg-white/5' : ''}`}>
            <div
              className="flex justify-between items-center p-4 cursor-pointer"
              onClick={() => toggleDropdown('media')}
            >
              <span className={`text-${scrolling ? 'gray-700' : 'white'} font-medium`}>{translations.navbar.media}</span>
              <div className="w-8 h-8 rounded-full bg-[#FDA729] flex items-center justify-center shrink-0">
                <ChevronDown className={`text-white transform transition-transform duration-200 ${activeDropdown === 'media' ? 'rotate-180' : ''}`} />
              </div>
            </div>
            <div className={`transition-all duration-300 ease-in-out ${activeDropdown === 'media' ? 'max-h-40 opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
              <div className="flex flex-col px-4 space-y-3 pl-8">
                <div className="flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/media/news-release" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.media.newsReleases}</Link>
                </div>
                <div className="flex items-center group">
                  <div
                    className="text-white h-6 rounded-full bg-[#FDA729] flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 w-0 group-hover:w-6 group-hover:mr-2"
                  >
                    <ArrowRight />
                  </div>
                  <Link to="/media/our-stories" className={`text-${scrolling ? 'gray-700' : 'white'} font-medium text-sm block`} onClick={closeAll}>{translations.media.ourStories}</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Career */}
          <Link to="/career" className={`rounded-xl border border-${scrolling ? 'gray-700' : 'white/20'} p-4 flex justify-between items-center text-${scrolling ? 'gray-700' : 'white'} font-medium`} onClick={closeAll}>
            {translations.navbar.career}
            <div className="w-8 h-8 rounded-full bg-[#FDA729] flex items-center justify-center shrink-0 text-white">
              <ArrowRight />
            </div>
          </Link>

        </div>

        {/* Footer CTA */}
        <div className="mt-auto pt-8 flex items-center gap-3 group">
          <Link to="/contact" className="border-2 border-[#28357B] bg-white group-hover:bg-[#28357B] text-[#28357B] group-hover:text-white px-8 py-3 rounded-full font-bold text-sm hover:bg-[#28357B/50] transition-colors" onClick={closeAll}>
            {translations.navbar.contact}
          </Link>
          <Link to="/contact" className="w-12 h-12 bg-[#28357B] group-hover:bg-[#FDA729] rounded-full flex items-center justify-center text-white hover:bg-[#002540] transition-colors" onClick={closeAll}>
            <ArrowRight />
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;